import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Panel } from '../cc/Panel'
import { EmptyState } from '../cc/EmptyState'
import { EYEBROW } from '../cc/tokens'
import { NagpurTripReplayMap } from './NagpurTripReplayMap'
import {
  fetchNagpurTripAlertReplay,
  fetchNagpurTripAlerts,
  type NagpurTripAlertReplay,
  type NagpurTripAlertRow,
} from './useNagpurTripAlerts'

const PAGE_SIZE = 50

const ALERT_COLOR_HARSH_BRAKE = '#F43F5E'
const ALERT_COLOR_HARSH_ACCEL = '#F59E0B'

/** started_ts/ended_ts arrive as epoch ms (null only when the stored clock text failed
 *  to parse -- serving/app.py's _parse_ist_instant / the view's regex-guarded cast). */
function fmtTime(ts: number | null): string {
  if (ts == null) return '—'
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * Nagpur's "Alerts per Trip" -- one row per EDMS-scheduled, dispatch-selected trip, with
 * harsh-brake/harsh-accel counts matched into that trip's own window. Modeled on
 * MbmtTripAlerts.tsx's interaction (poll, paginate, a request-id guard so a slow response
 * for a page the operator already left can never clobber the page they're on), not a copy
 * of its JSX -- Nagpur has no OVERSPEED/UNSCHEDULED_STOP/EXCESSIVE_IDLING kinds to render
 * and no live-roster driver fallback to reconcile (the duty row's own driver_name is
 * always the answer here).
 */
export function NagpurTripAlerts() {
  const [rows, setRows] = useState<NagpurTripAlertRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [replayFor, setReplayFor] = useState<NagpurTripAlertRow | null>(null)
  const requestIdRef = useRef(0)

  const load = useCallback((nextOffset: number) => {
    const myId = ++requestIdRef.current
    setLoading(true)
    fetchNagpurTripAlerts({ limit: PAGE_SIZE, offset: nextOffset })
      .then((page) => {
        if (requestIdRef.current !== myId) return // a newer request has already landed
        setRows(page.rows)
        setTotal(page.total)
        setError(null)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (requestIdRef.current !== myId) return
        setError(e instanceof Error ? e.message : String(e))
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    load(offset)
  }, [offset, load])

  const canPrev = offset > 0
  const canNext = offset + PAGE_SIZE < total

  return (
    <Panel title="Alerts per trip" right={<span className={EYEBROW}>{total} trips</span>}>
      {!loading && rows.length === 0 ? (
        <EmptyState title="No trips in range" body={error ?? undefined} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#94A3B8]">
                <th className="py-1 pr-3">Vehicle</th>
                <th className="py-1 pr-3">Driver</th>
                <th className="py-1 pr-3">Schedule</th>
                <th className="py-1 pr-3">Route</th>
                <th className="py-1 pr-3">Window</th>
                <th className="py-1 pr-3 text-right">Harsh brake</th>
                <th className="py-1 pr-3 text-right">Harsh accel</th>
                <th className="py-1 pr-3 text-right">Total</th>
                <th className="py-1 text-right">Replay</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.trip_id} className="border-t border-[rgba(15,23,42,0.06)]">
                  <td className="py-1.5 pr-3 font-medium text-[#0F172A]">{r.vehicle_id}</td>
                  <td className="py-1.5 pr-3">{r.driver_name ?? '—'}</td>
                  <td className="py-1.5 pr-3">{r.schedule_code ?? '—'}</td>
                  <td className="py-1.5 pr-3">{r.route ?? '—'}</td>
                  <td className="py-1.5 pr-3">
                    {fmtTime(r.started_ts)} – {fmtTime(r.ended_ts)}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{r.harsh_brake}</td>
                  <td className="py-1.5 pr-3 text-right">{r.harsh_accel}</td>
                  <td className="py-1.5 pr-3 text-right font-medium">{r.total_alerts}</td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      className="rounded-md border border-[rgba(15,23,42,0.1)] px-2 py-0.5 text-[11px] hover:bg-[#F8FAFC]"
                      onClick={() => setReplayFor(r)}
                      title={
                        r.total_alerts === 0
                          ? "No alerts on this trip, but its GPS trail can still be replayed"
                          : "Replay this trip's GPS path and alerts"
                      }
                    >
                      Replay
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          className="rounded-md border border-[rgba(15,23,42,0.1)] px-2 py-1 text-[11px] disabled:opacity-40"
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          disabled={!canPrev}
        >
          Prev
        </button>
        <button
          type="button"
          className="rounded-md border border-[rgba(15,23,42,0.1)] px-2 py-1 text-[11px] disabled:opacity-40"
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
          disabled={!canNext}
        >
          Next
        </button>
      </div>

      {replayFor && (
        <NagpurTripAlertReplayModal row={replayFor} onClose={() => setReplayFor(null)} />
      )}
    </Panel>
  )
}

/** The trip's GPS path + alert markers, rendered by NagpurTripReplayMap -- see that
 * file for why the map itself lives in a separate component. */
function NagpurTripAlertReplayModal({ row, onClose }: {
  row: NagpurTripAlertRow
  onClose: () => void
}): JSX.Element {
  const [clip, setClip] = useState<NagpurTripAlertReplay | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchNagpurTripAlertReplay(row.trip_id)
      .then((c) => { if (live) { setClip(c); setErr(null) } })
      .catch((e: unknown) => { if (live) setErr(e instanceof Error ? e.message : 'replay failed to load') })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [row.trip_id])

  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const lineLen = clip?.points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon)).length ?? 0

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${row.trip_id} replay`}
        className="flex h-[min(88vh,760px)] w-full max-w-4xl flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex shrink-0 flex-wrap items-start gap-3 border-b border-slate-200/80 px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <p className={EYEBROW}>Trip replay</p>
            <h2 className="mt-0.5 truncate text-[14px] font-semibold text-[#0F172A]">
              {row.vehicle_id} · {row.origin_stage || '—'} - {row.destination_stage || '—'}
            </h2>
            <p className="mt-0.5 text-[11px] text-[#64748B]">
              {row.schedule_code || 'no schedule'} · {row.route || 'no route on record'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {row.harsh_brake > 0 && (
              <span
                className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                style={{ backgroundColor: ALERT_COLOR_HARSH_BRAKE }}
              >
                Harsh brake · {row.harsh_brake}
              </span>
            )}
            {row.harsh_accel > 0 && (
              <span
                className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                style={{ backgroundColor: ALERT_COLOR_HARSH_ACCEL }}
              >
                Harsh accel · {row.harsh_accel}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="relative min-h-0 flex-1">
          {loading && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white/70 text-[12px] font-medium text-[#64748B]">
              Loading replay…
            </div>
          )}
          {err && (
            <div className="absolute left-3 top-3 z-10 rounded-[10px] border border-[rgba(244,63,94,0.26)] bg-[rgba(244,63,94,0.10)] px-3 py-2 text-[12px] font-medium text-[#9F1239]">
              {err}
            </div>
          )}
          {!loading && !err && lineLen < 2 && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white/90 text-[12px] text-[#94A3B8]">
              No GPS trail recorded for this trip.
            </div>
          )}
          <NagpurTripReplayMap clip={clip} />
        </div>
      </div>
    </div>
  )
}
